import { StringToUInt8 } from "../bytes/StringToUInt8"
import { UInt8ToString } from "../bytes/Uint8ToString"
import { Base64URLDecode } from "../encoding/Base64URLDecode"
import { GUIDToUint8 } from "../encoding/GUIDToUint8"
import { JSONDecode } from "../encoding/JSONDecode"
import { HMACSha256 } from "../hashing/HMACSha256"
import { UnixTime } from "../time/UnixTime"
import type { JWTHeader } from "../types/JWT"

/**
 * Verifies and decodes JWT token using HMAC-SHA256.
 *
 * Validates signature, expiration (exp), and issued-at (iat) claims.
 * Throws on invalid signature, expired, or future tokens.
 *
 * @param token - JWT string to decode
 * @param secret - Secret key for verification
 * @returns Promise resolving to decoded payload (without iat/exp)
 * @throws Error if signature invalid, token expired, or issued in future
 *
 * @example
 * ```ts
 * import { DecodeJWT } from "@pdfking/tfunc"
 *
 * const payload = await DecodeJWT<{ userId: number }>(token, "secret")
 * console.log(payload.userId) // 123
 * ```
 */
// Compare two Uint8Arrays for equality
const compareUint8Arrays = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

// JWT decoding function
export const DecodeJWT = async <T>(
  token: string,
  secret: string
): Promise<T> => {
  // Split token into parts
  const parts = token.split(".")
  if (parts.length !== 3) {
    throw new Error("Invalid JWT format")
  }

  const [encodedHeader, encodedPayload, encodedSignature] = parts

  // Decode header and payload
  const headerBytes = Base64URLDecode(encodedHeader)
  const payloadBytes = Base64URLDecode(encodedPayload)
  const providedSignature = Base64URLDecode(encodedSignature)

  const { alg } = JSONDecode(UInt8ToString(headerBytes)) as JWTHeader

  // Verify algorithm
  if (alg !== "HS256") {
    throw new Error("The server only supports HS256")
  }

  // Recreate the message to verify
  const message = `${encodedHeader}.${encodedPayload}`
  const messageBytes = StringToUInt8(message)
  const secretBytes = StringToUInt8(secret)

  // Compute expected signature
  const expectedSignatureGuid = await HMACSha256(secretBytes, messageBytes)
  const expectedSignature = GUIDToUint8(expectedSignatureGuid)

  // Compare signatures
  const valid = compareUint8Arrays(providedSignature, expectedSignature)

  if (!valid) {
    throw new Error("Invalid JWT signature")
  }

  const { iat, exp, ...payload } = JSONDecode(UInt8ToString(payloadBytes))

  // Validate JWT standard claims
  const now = UnixTime()

  // Check if token has expired
  if (exp && exp < now) {
    throw new Error("JWT token has expired")
  }

  // Check if token was issued in the future (clock skew protection)
  if (iat && iat > now + 60) {
    // Allow 60 seconds skew
    throw new Error("JWT token issued in the future")
  }

  // Remove JWT standard claims from payload before returning
  return payload as T
}
